import { Country } from '../helpers/enums'
import { formatDateStringToLocalDate } from '../helpers/utils'
import { TEducationalInfo, TProfessionalInfo, TRegisterFullInfo } from '../hooks/useFormRegister'
import { UserDTO } from './users'

export interface AuthenticatedUserData {
    picture: string
    fullName: string
}

export interface LoginResponse {
    token: string
    expiresIn: number
    picture: string
    fullName: string
    validated: boolean
    approved: boolean
}

export interface LinkedInLoginResponse {
    isNewUser: boolean
    loginResponseDTO: LoginResponse
    userResponseDTO: UserDTO
}

class ProfessionalInfoDTO {
    employerName: string
    jobTitle: string
    startDate: string
    endDate?: string
    currentJob: boolean
    employerCountry: string
    description: string
    abilities: string[]

    constructor(info: TProfessionalInfo) {
        this.employerName = info.companyName!
        this.jobTitle = info.job!
        this.startDate = formatDateStringToLocalDate(info.startDate!)
        this.endDate = info.endingDate ? formatDateStringToLocalDate(info.endingDate) : undefined
        this.currentJob = info.endingDate === ''
        this.employerCountry = info.country!
        this.description = info.responsabilities!
        this.abilities = info.abilities ?? []
    }
}

class EducationalInfoDTO {
    degree: string
    school: string
    startDate: string
    endDate?: string
    country: string
    areaOfStudy: string
    abilities: string[]

    constructor(info: TEducationalInfo) {
        this.degree = info.degree!
        this.school = info.school!
        this.startDate = formatDateStringToLocalDate(info.startDate!)
        this.endDate = info.endingDate ? formatDateStringToLocalDate(info.endingDate) : undefined
        this.country = info.country!
        this.areaOfStudy = info.carrer!
        this.abilities = info.abilities ?? []
    }
}

export const getDocumentType = (documentType: string) => {
    switch (documentType) {
        case 'DNI':
            return 'DNI'
        case 'Pasaporte':
            return 'Pasaporte'
        case 'Cédula de Identidad':
            return 'CI'
        case 'Libreta de Enrolamiento':
            return 'LE'
        case 'Libreta Cívica':
            return 'LC'
        case 'Otro':
            return 'Otro'
        default:
            return 'DNI'
    }
}

export class RegisterInfoDTO {
    firstName: string
    lastName: string
    email: string
    password: string
    documentType: string
    documentValue: string
    dateOfBirth: string
    phone: string
    nationality: number
    livesAbroad: boolean
    educationalHistories: Array<EducationalInfoDTO>
    workHistories: Array<ProfessionalInfoDTO>
    picture: string
    urlUserLinkedIn: string
    abilities: string[]

    constructor(info: TRegisterFullInfo) {
        this.firstName = info.personalInfo.firstName
        this.lastName = info.personalInfo.lastName
        this.email = info.registerInfo.email
        this.password = info.registerInfo.password
        this.documentType = getDocumentType(info.registerInfo.documentType)
        this.documentValue = info.registerInfo.documentValue
        this.picture = info.personalInfo.image
        this.dateOfBirth = formatDateStringToLocalDate(info.personalInfo.birthdate)
        this.phone = info.personalInfo.phone
        this.nationality = Country[info.personalInfo.nationality as keyof typeof Country]
        this.livesAbroad = info.personalInfo.livesAbroad
        this.educationalHistories = info.educationalInfo.map((item) => new EducationalInfoDTO(item))
        this.workHistories = info.professionalInfo.map((item) => new ProfessionalInfoDTO(item))
        this.urlUserLinkedIn = info.registerInfo.urlUserLinkedIn as string
        this.abilities = info.personalInfo.abilities
    }
}

export interface VerifyExistenceResponse {
    emailAvailable: boolean
    dniAvailable: boolean
}

export interface AuthToken {
    sub: string
    isAdmin: boolean
}
